import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Shield, Play, CheckCircle2, XCircle, AlertTriangle, Clock,
  Database, Users, Building2, CreditCard, BookOpen, Gamepad2,
  FileText, Bell, Settings, Loader2, Download,
  Server, Lock, BarChart3, Zap, Gauge, Timer, TrendingUp
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";

type TestStatus = "pending" | "running" | "pass" | "fail" | "warning";

interface TestResult {
  id: string;
  module: string;
  icon: any;
  name: string;
  status: TestStatus;
  message: string;
  duration?: number;
  details?: string[];
}

interface PerfBenchmark {
  table: string;
  label: string;
  selectTime: number;
  countTime: number;
  filterTime: number;
  rowCount: number;
  rating: "fast" | "normal" | "slow";
}

const PERF_TABLES = [
  { table: "nurseries", label: "الحضانات", filterCol: "name" },
  { table: "profiles", label: "المستخدمون", filterCol: "full_name" },
  { table: "children", label: "الأطفال", filterCol: "full_name" },
  { table: "classes", label: "الفصول", filterCol: "name" },
  { table: "staff", label: "الموظفون", filterCol: "full_name" },
  { table: "lessons", label: "الدروس", filterCol: "title" },
  { table: "exams", label: "الامتحانات", filterCol: "title" },
  { table: "questions", label: "الأسئلة", filterCol: "question_text" },
  { table: "assignments", label: "الواجبات", filterCol: "title" },
  { table: "invoices", label: "الفواتير", filterCol: "title" },
  { table: "attendance", label: "الحضور", filterCol: "status" },
  { table: "notifications", label: "الإشعارات", filterCol: "title" },
  { table: "messages", label: "الرسائل", filterCol: "content" },
  { table: "game_results", label: "نتائج الألعاب", filterCol: "game_type" },
  { table: "nursery_subscriptions", label: "الاشتراكات", filterCol: "plan" },
  { table: "support_tickets", label: "تذاكر الدعم", filterCol: "subject" },
] as const;

const initialTests: Omit<TestResult, "status" | "message" | "duration" | "details">[] = [
  { id: "db_connection", module: "البنية التحتية", icon: Database, name: "اتصال قاعدة البيانات" },
  { id: "auth_service", module: "البنية التحتية", icon: Lock, name: "خدمة المصادقة" },
  { id: "storage_service", module: "البنية التحتية", icon: Server, name: "خدمة التخزين" },
  { id: "nurseries", module: "المستخدمون والمحتوى", icon: Building2, name: "جدول الحضانات" },
  { id: "profiles", module: "المستخدمون والمحتوى", icon: Users, name: "جدول المستخدمين" },
  { id: "children", module: "المستخدمون والمحتوى", icon: Users, name: "جدول الأطفال" },
  { id: "classes", module: "المستخدمون والمحتوى", icon: BookOpen, name: "جدول الفصول" },
  { id: "staff", module: "المستخدمون والمحتوى", icon: Users, name: "جدول الموظفين" },
  { id: "user_roles", module: "الأمان", icon: Shield, name: "جدول الأدوار" },
  { id: "rls_check", module: "الأمان", icon: Lock, name: "سياسات أمان الصفوف (RLS)" },
  { id: "subscriptions", module: "الاشتراكات", icon: CreditCard, name: "جدول الاشتراكات" },
  { id: "subscription_health", module: "الاشتراكات", icon: CreditCard, name: "صحة الاشتراكات" },
  { id: "invoices", module: "المالية", icon: FileText, name: "جدول الفواتير" },
  { id: "lessons", module: "المحتوى التعليمي", icon: BookOpen, name: "جدول الدروس" },
  { id: "exams", module: "المحتوى التعليمي", icon: BookOpen, name: "جدول الامتحانات" },
  { id: "questions", module: "المحتوى التعليمي", icon: BookOpen, name: "بنك الأسئلة" },
  { id: "assignments", module: "المحتوى التعليمي", icon: FileText, name: "جدول الواجبات" },
  { id: "game_results", module: "الألعاب", icon: Gamepad2, name: "نتائج الألعاب" },
  { id: "attendance", module: "الحضور", icon: BarChart3, name: "جدول الحضور" },
  { id: "notifications", module: "الإشعارات", icon: Bell, name: "جدول الإشعارات" },
  { id: "messages", module: "الإشعارات", icon: Bell, name: "جدول الرسائل" },
  { id: "support_tickets", module: "الدعم الفني", icon: Settings, name: "تذاكر الدعم" },
  { id: "edge_functions", module: "الوظائف السحابية", icon: Zap, name: "وظائف Edge Functions" },
];

const getRating = (ms: number): "fast" | "normal" | "slow" => {
  if (ms < 200) return "fast";
  if (ms < 500) return "normal";
  return "slow";
};

const ratingColor = (r: "fast" | "normal" | "slow") => {
  if (r === "fast") return "hsl(142, 71%, 45%)";
  if (r === "normal") return "hsl(38, 92%, 50%)";
  return "hsl(0, 84%, 60%)";
};

const ratingLabel = (r: "fast" | "normal" | "slow") => {
  if (r === "fast") return "سريع ⚡";
  if (r === "normal") return "عادي 🟡";
  return "بطيء 🔴";
};

const AdminSystemTest = () => {
  const [tests, setTests] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState("health");

  // Performance state
  const [perfResults, setPerfResults] = useState<PerfBenchmark[]>([]);
  const [isPerfRunning, setIsPerfRunning] = useState(false);
  const [perfProgress, setPerfProgress] = useState(0);
  const [perfStartTime, setPerfStartTime] = useState<Date | null>(null);
  const [perfEndTime, setPerfEndTime] = useState<Date | null>(null);

  const updateTest = useCallback((id: string, update: Partial<TestResult>) => {
    setTests(prev => prev.map(t => t.id === id ? { ...t, ...update } : t));
  }, []);

  const runTest = async (testId: string): Promise<Partial<TestResult>> => {
    try {
      switch (testId) {
        case "db_connection": {
          const { error } = await supabase.from("nurseries").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: "الاتصال بقاعدة البيانات يعمل بشكل طبيعي" };
        }
        case "auth_service": {
          const { data } = await supabase.auth.getSession();
          if (!data.session) return { status: "warning", message: "لا توجد جلسة نشطة - تأكد من تسجيل الدخول" };
          return { status: "pass", message: "خدمة المصادقة تعمل بشكل طبيعي" };
        }
        case "storage_service": {
          const { data, error } = await supabase.storage.listBuckets();
          if (error) throw error;
          return { status: "pass", message: `${data?.length || 0} حاوية تخزين متاحة`, details: data?.map(b => b.name) };
        }
        case "nurseries": {
          const { count, error } = await supabase.from("nurseries").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: (count || 0) > 0 ? "pass" : "warning", message: `${count || 0} حضانة مسجلة` };
        }
        case "profiles": {
          const { count, error } = await supabase.from("profiles").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} مستخدم مسجل` };
        }
        case "children": {
          const { count, error } = await supabase.from("children").select("id", { count: "exact", head: true });
          if (error) throw error;
          const { count: orphanCount } = await supabase.from("children").select("id", { count: "exact", head: true }).is("parent_id", null);
          const details: string[] = [];
          if ((orphanCount || 0) > 0) details.push(`${orphanCount} طفل بدون ولي أمر مربوط`);
          return { status: (orphanCount || 0) > 0 ? "warning" : "pass", message: `${count || 0} طفل مسجل`, details: details.length > 0 ? details : undefined };
        }
        case "classes": {
          const { count, error } = await supabase.from("classes").select("id", { count: "exact", head: true });
          if (error) throw error;
          const { count: noTeacher } = await supabase.from("classes").select("id", { count: "exact", head: true }).is("teacher_id", null);
          const details: string[] = [];
          if ((noTeacher || 0) > 0) details.push(`${noTeacher} فصل بدون معلم`);
          return { status: (noTeacher || 0) > 0 ? "warning" : "pass", message: `${count || 0} فصل دراسي`, details: details.length > 0 ? details : undefined };
        }
        case "staff": {
          const { count, error } = await supabase.from("staff").select("id", { count: "exact", head: true });
          if (error) throw error;
          const { count: noAccount } = await supabase.from("staff").select("id", { count: "exact", head: true }).is("user_id", null);
          const details: string[] = [];
          if ((noAccount || 0) > 0) details.push(`${noAccount} موظف بدون حساب دخول`);
          return { status: "pass", message: `${count || 0} موظف`, details: details.length > 0 ? details : undefined };
        }
        case "user_roles": {
          const { count, error } = await supabase.from("user_roles").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} دور مُعيَّن` };
        }
        case "rls_check": {
          const { error } = await supabase.from("nurseries").select("id").limit(2);
          if (error) throw error;
          return { status: "pass", message: "سياسات أمان الصفوف تعمل بشكل صحيح", details: ["الوصول كمشرف عام يعمل بشكل طبيعي"] };
        }
        case "subscriptions": {
          const { count, error } = await supabase.from("nursery_subscriptions").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} اشتراك` };
        }
        case "subscription_health": {
          const { data, error } = await supabase.from("nursery_subscriptions").select("status, expires_at");
          if (error) throw error;
          const subs = data || [];
          const expired = subs.filter(s => s.expires_at && new Date(s.expires_at) < new Date());
          const active = subs.filter(s => s.status === "active");
          return { status: expired.length > active.length ? "warning" : "pass", message: `${active.length}/${subs.length} اشتراك نشط`, details: [`${active.length} اشتراك نشط`, `${expired.length} اشتراك منتهي`] };
        }
        case "invoices": {
          const { count, error } = await supabase.from("invoices").select("id", { count: "exact", head: true });
          if (error) throw error;
          const { count: pending } = await supabase.from("invoices").select("id", { count: "exact", head: true }).eq("status", "pending");
          const details: string[] = [];
          if ((pending || 0) > 0) details.push(`${pending} فاتورة معلقة`);
          return { status: "pass", message: `${count || 0} فاتورة`, details: details.length > 0 ? details : undefined };
        }
        case "lessons": {
          const { count, error } = await supabase.from("lessons").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} درس` };
        }
        case "exams": {
          const { count, error } = await supabase.from("exams").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} امتحان` };
        }
        case "questions": {
          const { count, error } = await supabase.from("questions").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} سؤال في البنك` };
        }
        case "assignments": {
          const { count, error } = await supabase.from("assignments").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} واجب` };
        }
        case "game_results": {
          const { count, error } = await supabase.from("game_results").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} نتيجة لعبة مسجلة` };
        }
        case "attendance": {
          const { count, error } = await supabase.from("attendance").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} سجل حضور` };
        }
        case "notifications": {
          const { count, error } = await supabase.from("notifications").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} إشعار` };
        }
        case "messages": {
          const { count, error } = await supabase.from("messages").select("id", { count: "exact", head: true });
          if (error) throw error;
          return { status: "pass", message: `${count || 0} رسالة` };
        }
        case "support_tickets": {
          const { count, error } = await supabase.from("support_tickets").select("id", { count: "exact", head: true });
          if (error) throw error;
          const { count: openCount } = await supabase.from("support_tickets").select("id", { count: "exact", head: true }).eq("status", "open");
          const details: string[] = [];
          if ((openCount || 0) > 0) details.push(`${openCount} تذكرة مفتوحة`);
          return { status: "pass", message: `${count || 0} تذكرة دعم`, details: details.length > 0 ? details : undefined };
        }
        case "edge_functions": {
          const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
          if (!projectId) return { status: "warning", message: "لم يتم تحديد معرف المشروع" };
          try {
            const res = await fetch(`https://${projectId}.supabase.co/functions/v1/check-expiring-subscriptions`, { method: "OPTIONS" });
            return { status: res.ok || res.status === 204 ? "pass" : "warning", message: "خدمة الوظائف السحابية متاحة" };
          } catch {
            return { status: "warning", message: "لم يتم التحقق من الوظائف السحابية" };
          }
        }
        default:
          return { status: "warning", message: "اختبار غير معرّف" };
      }
    } catch (err: any) {
      return { status: "fail", message: err.message || "خطأ غير متوقع", details: [err.code, err.hint].filter(Boolean) };
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setStartTime(new Date());
    setEndTime(null);
    setProgress(0);
    setActiveTab("health");

    const initialResults: TestResult[] = initialTests.map(t => ({
      ...t, status: "pending" as TestStatus, message: "في الانتظار...",
    }));
    setTests(initialResults);

    for (let i = 0; i < initialTests.length; i++) {
      const test = initialTests[i];
      updateTest(test.id, { status: "running", message: "جارٍ الاختبار..." });
      const start = performance.now();
      const result = await runTest(test.id);
      const duration = Math.round(performance.now() - start);
      updateTest(test.id, { ...result, duration });
      setProgress(Math.round(((i + 1) / initialTests.length) * 100));
      await new Promise(r => setTimeout(r, 80));
    }

    setEndTime(new Date());
    setIsRunning(false);
  };

  // Performance benchmark
  const benchmarkTable = async (table: string, filterCol: string): Promise<Omit<PerfBenchmark, "label">> => {
    // 1. SELECT * LIMIT 50
    const t1 = performance.now();
    const { error: e1 } = await supabase.from(table as any).select("*").limit(50);
    const selectTime = Math.round(performance.now() - t1);

    // 2. COUNT
    const t2 = performance.now();
    const { count, error: e2 } = await supabase.from(table as any).select("id", { count: "exact", head: true });
    const countTime = Math.round(performance.now() - t2);

    // 3. FILTER query
    const t3 = performance.now();
    const { error: e3 } = await supabase.from(table as any).select("id").ilike(filterCol, "%test%").limit(10);
    const filterTime = Math.round(performance.now() - t3);

    const avgTime = (selectTime + countTime + filterTime) / 3;

    if (e1 || e2 || e3) {
      return { table, selectTime: -1, countTime: -1, filterTime: -1, rowCount: 0, rating: "slow" };
    }

    return {
      table,
      selectTime,
      countTime,
      filterTime,
      rowCount: count || 0,
      rating: getRating(avgTime),
    };
  };

  const runPerformanceTests = async () => {
    setIsPerfRunning(true);
    setPerfStartTime(new Date());
    setPerfEndTime(null);
    setPerfProgress(0);
    setPerfResults([]);
    setActiveTab("performance");

    const results: PerfBenchmark[] = [];

    for (let i = 0; i < PERF_TABLES.length; i++) {
      const { table, label, filterCol } = PERF_TABLES[i];
      const result = await benchmarkTable(table, filterCol);
      results.push({ ...result, label });
      setPerfResults([...results]);
      setPerfProgress(Math.round(((i + 1) / PERF_TABLES.length) * 100));
      await new Promise(r => setTimeout(r, 50));
    }

    setPerfEndTime(new Date());
    setIsPerfRunning(false);
  };

  const exportReport = () => {
    const lines = [
      "تقرير الفحص الشامل للنظام - NurseryNet",
      `التاريخ: ${format(startTime || new Date(), "yyyy-MM-dd HH:mm:ss")}`,
      `المدة: ${endTime && startTime ? ((endTime.getTime() - startTime.getTime()) / 1000).toFixed(1) : "-"}s`,
      "", "النتائج:", "═".repeat(60),
    ];

    tests.forEach(t => {
      const icon = t.status === "pass" ? "✅" : t.status === "fail" ? "❌" : "⚠️";
      lines.push(`${icon} [${t.module}] ${t.name}: ${t.message} (${t.duration}ms)`);
      t.details?.forEach(d => lines.push(`   → ${d}`));
    });

    if (perfResults.length > 0) {
      lines.push("", "", "═══ اختبارات الأداء ═══", "═".repeat(60));
      lines.push("الجدول | SELECT | COUNT | FILTER | الصفوف | التقييم");
      lines.push("-".repeat(60));
      perfResults.forEach(p => {
        lines.push(`${p.label} | ${p.selectTime}ms | ${p.countTime}ms | ${p.filterTime}ms | ${p.rowCount} | ${ratingLabel(p.rating)}`);
      });
      const slowTables = perfResults.filter(p => p.rating === "slow");
      if (slowTables.length > 0) {
        lines.push("", "⚠️ جداول بطيئة تحتاج مراجعة:");
        slowTables.forEach(s => lines.push(`  - ${s.label} (متوسط: ${Math.round((s.selectTime + s.countTime + s.filterTime) / 3)}ms)`));
      }
    }

    lines.push("", "═".repeat(60));
    const passed = tests.filter(t => t.status === "pass").length;
    const failed = tests.filter(t => t.status === "fail").length;
    const warnings = tests.filter(t => t.status === "warning").length;
    lines.push(`الملخص: ✅ ${passed} ناجح | ❌ ${failed} فاشل | ⚠️ ${warnings} تحذير`);

    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `system-test-${format(new Date(), "yyyy-MM-dd-HHmm")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const passed = tests.filter(t => t.status === "pass").length;
  const failed = tests.filter(t => t.status === "fail").length;
  const warnings = tests.filter(t => t.status === "warning").length;

  const statusIcon = (s: TestStatus) => {
    switch (s) {
      case "pass": return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case "fail": return <XCircle className="w-5 h-5 text-red-500" />;
      case "warning": return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case "running": return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default: return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const modules = [...new Set(initialTests.map(t => t.module))];

  // Performance chart data
  const perfChartData = perfResults.map(p => ({
    name: p.label,
    select: p.selectTime,
    count: p.countTime,
    filter: p.filterTime,
    avg: Math.round((p.selectTime + p.countTime + p.filterTime) / 3),
  }));

  const perfRadarData = perfResults.map(p => ({
    table: p.label,
    speed: Math.max(0, 500 - Math.round((p.selectTime + p.countTime + p.filterTime) / 3)),
  }));

  const fastCount = perfResults.filter(p => p.rating === "fast").length;
  const normalCount = perfResults.filter(p => p.rating === "normal").length;
  const slowCount = perfResults.filter(p => p.rating === "slow").length;
  const avgResponseTime = perfResults.length > 0
    ? Math.round(perfResults.reduce((sum, p) => sum + (p.selectTime + p.countTime + p.filterTime) / 3, 0) / perfResults.length)
    : 0;

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Shield className="w-7 h-7 text-amber-500" />
            اختبار شامل للنظام
          </h1>
          <p className="text-muted-foreground mt-1">فحص جميع الموديولات والخدمات وقياس أداء الجداول</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(tests.length > 0 || perfResults.length > 0) && !isRunning && !isPerfRunning && (
            <Button variant="outline" onClick={exportReport} className="gap-2">
              <Download className="w-4 h-4" />
              تصدير التقرير
            </Button>
          )}
          <Button
            onClick={runPerformanceTests}
            disabled={isRunning || isPerfRunning}
            variant="outline"
            className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            {isPerfRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gauge className="w-4 h-4" />}
            {isPerfRunning ? "قياس الأداء..." : "قياس الأداء"}
          </Button>
          <Button
            onClick={runAllTests}
            disabled={isRunning || isPerfRunning}
            className="gap-2 bg-amber-600 hover:bg-amber-700"
          >
            {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {isRunning ? "جارٍ الفحص..." : tests.length > 0 ? "إعادة الفحص" : "بدء الفحص"}
          </Button>
        </div>
      </div>

      {/* Progress bars */}
      {isRunning && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span>تقدم فحص الصحة</span>
              <span className="font-mono">{progress}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </CardContent>
        </Card>
      )}
      {isPerfRunning && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span>تقدم قياس الأداء</span>
              <span className="font-mono">{perfProgress}%</span>
            </div>
            <Progress value={perfProgress} className="h-3" />
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="health" className="gap-2">
            <CheckCircle2 className="w-4 h-4" />
            فحص الصحة ({tests.length > 0 ? `${passed}/${tests.length}` : "—"})
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2">
            <Gauge className="w-4 h-4" />
            اختبار الأداء ({perfResults.length > 0 ? `${perfResults.length} جدول` : "—"})
          </TabsTrigger>
        </TabsList>

        {/* ===== HEALTH TAB ===== */}
        <TabsContent value="health" className="space-y-6 mt-4">
          {/* Summary Cards */}
          {tests.length > 0 && !isRunning && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20">
                  <CardContent className="pt-6 text-center">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-emerald-600">{passed}</div>
                    <div className="text-sm text-muted-foreground">ناجح</div>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20">
                  <CardContent className="pt-6 text-center">
                    <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-red-600">{failed}</div>
                    <div className="text-sm text-muted-foreground">فاشل</div>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                  <CardContent className="pt-6 text-center">
                    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-amber-600">{warnings}</div>
                    <div className="text-sm text-muted-foreground">تحذير</div>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
                  <CardContent className="pt-6 text-center">
                    <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-blue-600">
                      {endTime && startTime ? ((endTime.getTime() - startTime.getTime()) / 1000).toFixed(1) : "-"}s
                    </div>
                    <div className="text-sm text-muted-foreground">المدة الإجمالية</div>
                  </CardContent>
                </Card>
              </div>

              {/* Overall Status Banner */}
              <Card className={
                failed > 0 ? "border-red-300 bg-red-50 dark:bg-red-950/30"
                  : warnings > 0 ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30"
                    : "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30"
              }>
                <CardContent className="pt-6 pb-6 text-center">
                  <div className="text-4xl mb-2">{failed > 0 ? "❌" : warnings > 0 ? "⚠️" : "✅"}</div>
                  <h2 className="text-xl font-bold">
                    {failed > 0 ? "توجد مشاكل تحتاج إصلاح!" : warnings > 0 ? "النظام يعمل مع بعض التنبيهات" : "النظام يعمل بشكل ممتاز! 🎉"}
                  </h2>
                  <p className="text-muted-foreground mt-1">{format(endTime || new Date(), "dd MMMM yyyy - HH:mm", { locale: ar })}</p>
                </CardContent>
              </Card>
            </>
          )}

          {/* Empty State */}
          {tests.length === 0 && !isRunning && (
            <Card className="border-dashed">
              <CardContent className="pt-12 pb-12 text-center">
                <Shield className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">لم يتم إجراء أي فحص بعد</h3>
                <p className="text-muted-foreground mb-4">اضغط على "بدء الفحص" لتشغيل اختبار شامل</p>
                <Button onClick={runAllTests} className="gap-2 bg-amber-600 hover:bg-amber-700">
                  <Play className="w-4 h-4" />
                  بدء الفحص الشامل
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Detailed Results by Module */}
          {tests.length > 0 && modules.map(mod => {
            const moduleTests = tests.filter(t => t.module === mod);
            return (
              <Card key={mod}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    {(() => { const Icon = moduleTests[0]?.icon || Database; return <Icon className="w-5 h-5 text-primary" />; })()}
                    {mod}
                    <Badge variant="outline" className="mr-auto">
                      {moduleTests.filter(t => t.status === "pass").length}/{moduleTests.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {moduleTests.map(test => (
                    <div
                      key={test.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                        test.status === "fail" ? "bg-red-50/50 border-red-200 dark:bg-red-950/20" :
                        test.status === "warning" ? "bg-amber-50/50 border-amber-200 dark:bg-amber-950/20" :
                        test.status === "pass" ? "bg-emerald-50/30 border-emerald-200 dark:bg-emerald-950/10" :
                        "bg-muted/30 border-border"
                      }`}
                    >
                      <div className="mt-0.5">{statusIcon(test.status)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{test.name}</span>
                          {test.duration !== undefined && (
                            <span className="text-xs text-muted-foreground font-mono">{test.duration}ms</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{test.message}</p>
                        {test.details && test.details.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {test.details.map((d, i) => (
                              <p key={i} className="text-xs text-muted-foreground/80">→ {d}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ===== PERFORMANCE TAB ===== */}
        <TabsContent value="performance" className="space-y-6 mt-4">
          {/* Performance Summary Cards */}
          {perfResults.length > 0 && !isPerfRunning && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20">
                  <CardContent className="pt-5 text-center">
                    <Zap className="w-7 h-7 text-emerald-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-emerald-600">{fastCount}</div>
                    <div className="text-xs text-muted-foreground">سريع ⚡</div>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                  <CardContent className="pt-5 text-center">
                    <Timer className="w-7 h-7 text-amber-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-amber-600">{normalCount}</div>
                    <div className="text-xs text-muted-foreground">عادي 🟡</div>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20">
                  <CardContent className="pt-5 text-center">
                    <AlertTriangle className="w-7 h-7 text-red-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-red-600">{slowCount}</div>
                    <div className="text-xs text-muted-foreground">بطيء 🔴</div>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
                  <CardContent className="pt-5 text-center">
                    <TrendingUp className="w-7 h-7 text-blue-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-blue-600">{avgResponseTime}ms</div>
                    <div className="text-xs text-muted-foreground">متوسط الاستجابة</div>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
                  <CardContent className="pt-5 text-center">
                    <Database className="w-7 h-7 text-purple-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold text-purple-600">
                      {perfResults.reduce((s, p) => s + p.rowCount, 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">إجمالي الصفوف</div>
                  </CardContent>
                </Card>
              </div>

              {/* Response Time Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    سرعة الاستجابة لكل جدول (بالمللي ثانية)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={perfChartData} layout="vertical" margin={{ right: 30, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" unit="ms" />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v: number) => `${v}ms`} />
                      <Bar dataKey="select" name="SELECT" fill="hsl(217, 91%, 60%)" radius={[0, 4, 4, 0]} barSize={12} />
                      <Bar dataKey="count" name="COUNT" fill="hsl(142, 71%, 45%)" radius={[0, 4, 4, 0]} barSize={12} />
                      <Bar dataKey="filter" name="FILTER" fill="hsl(280, 67%, 55%)" radius={[0, 4, 4, 0]} barSize={12} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Average Response Time Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-primary" />
                    متوسط زمن الاستجابة — تقييم الجداول
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={perfChartData.sort((a, b) => b.avg - a.avg)} margin={{ right: 20, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis unit="ms" />
                      <Tooltip formatter={(v: number) => `${v}ms`} />
                      <Bar dataKey="avg" name="متوسط" radius={[6, 6, 0, 0]} barSize={30}>
                        {perfChartData.sort((a, b) => b.avg - a.avg).map((entry, idx) => (
                          <Cell key={idx} fill={ratingColor(getRating(entry.avg))} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex gap-4 justify-center mt-3 text-xs">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> {"< 200ms سريع"}</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> {"200-500ms عادي"}</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> {"> 500ms بطيء"}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="w-5 h-5 text-primary" />
                    تفاصيل الأداء لكل جدول
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-right py-2 px-3">الجدول</th>
                          <th className="text-center py-2 px-3">الصفوف</th>
                          <th className="text-center py-2 px-3">SELECT</th>
                          <th className="text-center py-2 px-3">COUNT</th>
                          <th className="text-center py-2 px-3">FILTER</th>
                          <th className="text-center py-2 px-3">المتوسط</th>
                          <th className="text-center py-2 px-3">التقييم</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perfResults.map(p => {
                          const avg = Math.round((p.selectTime + p.countTime + p.filterTime) / 3);
                          return (
                            <tr key={p.table} className="border-b hover:bg-muted/30">
                              <td className="py-2 px-3 font-medium">{p.label}</td>
                              <td className="py-2 px-3 text-center font-mono text-xs">{p.rowCount.toLocaleString()}</td>
                              <td className="py-2 px-3 text-center font-mono text-xs">{p.selectTime}ms</td>
                              <td className="py-2 px-3 text-center font-mono text-xs">{p.countTime}ms</td>
                              <td className="py-2 px-3 text-center font-mono text-xs">{p.filterTime}ms</td>
                              <td className="py-2 px-3 text-center font-mono text-xs font-bold">{avg}ms</td>
                              <td className="py-2 px-3 text-center">
                                <Badge variant={p.rating === "fast" ? "default" : p.rating === "normal" ? "secondary" : "destructive"} className="text-xs">
                                  {ratingLabel(p.rating)}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Slow tables alert */}
              {slowCount > 0 && (
                <Card className="border-red-300 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-500 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-red-700">جداول بطيئة تحتاج مراجعة</h3>
                        <ul className="mt-2 space-y-1">
                          {perfResults.filter(p => p.rating === "slow").map(p => (
                            <li key={p.table} className="text-sm text-red-600">
                              • {p.label} — متوسط {Math.round((p.selectTime + p.countTime + p.filterTime) / 3)}ms ({p.rowCount} صف)
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-muted-foreground mt-2">
                          💡 يُنصح بإضافة فهارس (Indexes) أو مراجعة سياسات RLS للجداول البطيئة
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Performance Empty State */}
          {perfResults.length === 0 && !isPerfRunning && (
            <Card className="border-dashed">
              <CardContent className="pt-12 pb-12 text-center">
                <Gauge className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">لم يتم قياس الأداء بعد</h3>
                <p className="text-muted-foreground mb-4">
                  سيتم قياس سرعة SELECT و COUNT و FILTER لكل جدول وتحديد الجداول البطيئة
                </p>
                <Button onClick={runPerformanceTests} variant="outline" className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50">
                  <Gauge className="w-4 h-4" />
                  بدء قياس الأداء
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSystemTest;
